import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { 
  MessageCircle, Trash2, CheckCheck, RefreshCw, 
  Phone, Clock, User, Bell, Send, ArrowLeft, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const API = '/api';

function formatTime(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Ahora";
  if (diffMins < 60) return `${diffMins}m`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays < 7) return `${diffDays}d`;
  return date.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit" });
}

function formatFullTime(timestamp) {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleString("es-ES", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit"
  });
}

export default function Replies() {
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [contactName, setContactName] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [filter, setFilter] = useState({ profileId: "" });
  const [profiles, setProfiles] = useState([]);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const selectedPhoneRef = useRef(null);

  // Keep ref in sync
  useEffect(() => { selectedPhoneRef.current = selectedPhone; }, [selectedPhone]);

  const fetchConversations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filter.profileId) params.append("profile_id", filter.profileId);
      const res = await axios.get(`${API}/conversations?${params.toString()}`);
      setConversations(res.data.conversations);
      setTotalUnread(res.data.total_unread);
    } catch (e) {
      // error silenced
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const fetchProfiles = async () => {
    try {
      const res = await axios.get(`${API}/profiles`);
      setProfiles(res.data);
    } catch (e) {
      // error silenced
    }
  };

  const fetchMessages = useCallback(async (phone) => {
    setLoadingMessages(true);
    try {
      const res = await axios.get(`${API}/conversations/${encodeURIComponent(phone)}/messages`);
      setMessages(res.data.messages);
      setContactName(res.data.contact_name || phone);
      // Update unread count in conversations list
      setConversations(prev => prev.map(c => 
        c.phone === phone ? { ...c, unread_count: 0 } : c
      ));
      setTotalUnread(prev => {
        const conv = conversations.find(c => c.phone === phone);
        return Math.max(0, prev - (conv?.unread_count || 0));
      });
    } catch (e) {
      toast.error("Error al cargar mensajes");
    } finally {
      setLoadingMessages(false);
    }
  }, [conversations]);

  useEffect(() => {
    fetchConversations();
    fetchProfiles();
  }, [fetchConversations]);

  useEffect(() => {
    if (selectedPhone) fetchMessages(selectedPhone);
     
  }, [selectedPhone]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // WebSocket
  useEffect(() => {
    const wsUrl = `${window.location.origin.replace('http', 'ws')}/api/ws/global`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "new_reply") {
        fetchConversations();
        if (selectedPhoneRef.current && data.reply.phone === selectedPhoneRef.current) {
          setMessages(prev => [...prev, {
            id: data.reply.id,
            type: "incoming",
            text: data.reply.message_text,
            time: data.reply.received_at,
            read: true,
            contact_name: data.reply.contact_name,
            profile_name: data.reply.profile_name,
          }]);
        }
        toast.info(`${data.reply.contact_name || data.reply.phone}: ${data.reply.message_text.slice(0, 50)}`);
      }
    };
    const ping = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) ws.send("ping");
    }, 30000);
    return () => { clearInterval(ping); ws.close(); };
     
  }, []);

  const markAllAsRead = async () => {
    try {
      await axios.post(`${API}/replies/mark-all-read`);
      setConversations(prev => prev.map(c => ({ ...c, unread_count: 0 })));
      setTotalUnread(0);
      toast.success("Todo marcado como leído");
    } catch (e) { toast.error("Error"); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !selectedPhone) return;
    const profileId = conversations.find(c => c.phone === selectedPhone)?.profile_id;
    if (!profileId) {
      toast.error("No se encontró el perfil asociado");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/conversations/reply`, {
        phone: selectedPhone,
        message: replyText.trim(),
        profile_id: profileId
      });
      // Add outgoing message to chat
      setMessages(prev => [...prev, {
        id: Date.now().toString(),
        type: "outgoing",
        text: replyText.trim(),
        time: new Date().toISOString(),
      }]);
      setReplyText("");
      toast.success("Respuesta en cola de envío");
    } catch (e) {
      toast.error("Error al enviar respuesta");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div data-testid="replies-page" className="flex h-[calc(100vh-120px)] gap-0 overflow-hidden rounded-xl border border-zinc-800">
      {/* Left: Conversation List */}
      <div className={`${selectedPhone ? 'hidden md:flex' : 'flex'} flex-col w-full md:w-80 lg:w-96 border-r border-zinc-800 bg-zinc-950/50`}>
        {/* Header */}
        <div className="p-3 border-b border-zinc-800">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-bold text-white flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-cyan-500" />
              Conversaciones
              {totalUnread > 0 && (
                <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{totalUnread}</span>
              )}
            </h2>
            <div className="flex gap-1">
              <button onClick={fetchConversations} className="p-1.5 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg" data-testid="refresh-replies-btn">
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              {totalUnread > 0 && (
                <button onClick={markAllAsRead} className="p-1.5 text-zinc-400 hover:text-cyan-400 hover:bg-zinc-800 rounded-lg" data-testid="mark-all-read-btn" title="Marcar todo leído">
                  <CheckCheck className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <select
            value={filter.profileId}
            onChange={(e) => setFilter({ profileId: e.target.value })}
            className="w-full bg-zinc-900 border border-zinc-700 text-white rounded px-2 py-1 text-[11px]"
          >
            <option value="">Todos los perfiles</option>
            {profiles.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-6 text-center">
              <MessageCircle className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
              <p className="text-zinc-500 text-xs">Sin conversaciones</p>
            </div>
          ) : (
            conversations.map(conv => (
              <button
                key={conv.phone}
                data-testid={`conv-${conv.phone}`}
                onClick={() => setSelectedPhone(conv.phone)}
                className={`w-full text-left px-3 py-3 border-b border-zinc-800/50 transition-colors hover:bg-zinc-800/50 ${
                  selectedPhone === conv.phone ? 'bg-zinc-800/70 border-l-2 border-l-cyan-500' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {conv.unread_count > 0 && (
                        <span className="w-2 h-2 bg-cyan-500 rounded-full flex-shrink-0" />
                      )}
                      <span className={`text-sm truncate ${conv.unread_count > 0 ? 'font-bold text-white' : 'text-zinc-300'}`}>
                        {conv.contact_name || conv.phone}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-500 truncate mt-0.5">{conv.last_message}</p>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="text-[10px] text-zinc-600">{formatTime(conv.last_time)}</span>
                    {conv.unread_count > 0 && (
                      <span className="bg-cyan-500 text-black text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[9px] text-zinc-600 font-mono">{conv.phone}</span>
                  <span className="text-[9px] text-zinc-700">·</span>
                  <span className="text-[9px] text-zinc-600">{conv.profile_name}</span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Right: Chat / Messages */}
      <div className={`${selectedPhone ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-zinc-900/30`}>
        {!selectedPhone ? (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-600">
            <MessageCircle className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">Selecciona una conversación</p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-zinc-800 bg-zinc-950/50">
              <button
                onClick={() => setSelectedPhone(null)}
                className="md:hidden p-1 text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <User className="w-8 h-8 text-zinc-600 bg-zinc-800 rounded-full p-1.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-white">{contactName}</p>
                <p className="text-[11px] text-zinc-500 font-mono flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {selectedPhone}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {loadingMessages ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-cyan-500" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-8 text-zinc-600 text-sm">Sin mensajes</div>
              ) : (
                messages.map((msg) => (
                  <div
                    key={msg.id}
                    data-testid={`msg-${msg.id}`}
                    className={`flex ${msg.type === 'outgoing' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 ${
                      msg.type === 'outgoing'
                        ? 'bg-blue-600/80 text-white rounded-br-sm'
                        : 'bg-zinc-800 text-zinc-200 rounded-bl-sm'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>
                      <div className={`flex items-center gap-1.5 mt-1 ${
                        msg.type === 'outgoing' ? 'justify-end' : ''
                      }`}>
                        <span className="text-[9px] opacity-60">{formatFullTime(msg.time)}</span>
                        {msg.type === 'outgoing' && msg.status === 'pending' && (
                          <span className="text-[8px] bg-yellow-500/30 text-yellow-300 px-1 rounded">En cola</span>
                        )}
                        {msg.type === 'outgoing' && msg.rcs && (
                          <span className="text-[8px] bg-emerald-500/30 text-emerald-300 px-1 rounded">RCS</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            <div className="border-t border-zinc-800 px-3 py-2 bg-zinc-950/50">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="reply-input"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }}
                  rows={1}
                  className="flex-1 bg-zinc-800 border border-zinc-700 focus:border-cyan-500 rounded-xl px-3 py-2 text-sm text-white placeholder:text-zinc-600 outline-none resize-none max-h-24"
                  placeholder="Escribe una respuesta..."
                />
                <button
                  data-testid="send-reply-btn"
                  onClick={sendReply}
                  disabled={!replyText.trim() || sending}
                  className="p-2.5 bg-cyan-600 hover:bg-cyan-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white rounded-xl transition-colors"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              <p className="text-[9px] text-zinc-600 mt-1">Enter para enviar · Shift+Enter para nueva línea · Se enviará via la extensión</p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
